/**
 * The arithmetic behind laying life events over a chart, kept separate from
 * the SVG for the same reason chartMath.ts is: so the collision and clipping
 * rules can be read — and tested — without wading through markup.
 *
 * No JSX, no DOM, no React. Given the events, the chart's own time domain,
 * and the plot's pixel geometry, this reports what to draw and where —
 * drawing itself, and every colour and token, stays in MeasurementChart.tsx.
 */
import { isPeriod } from '@loggy/shared';
import type { LifeEvent } from '@loggy/shared';

/** The chart's time domain, ISO-8601, same shape the caller already has as
 *  `timeRange`/`[minT, maxT]` — kept as strings here so this module never
 *  has to agree with the caller about which Date epoch a bare number means. */
export interface EventLayoutDomain {
  from: string;
  to: string;
}

/** The plot's own pixel bounds — the SAME x0/x1 the series is drawn between,
 *  so an event lines up with the reading it sits beside. */
export interface EventLayoutPlot {
  x0: number;
  x1: number;
}

export interface EventLayoutOptions {
  /**
   * Approx pixel width of one character in the label font. There is no real
   * text measurement available outside the DOM for a pure function, so this
   * is a deliberately generous per-character estimate — it only has to avoid
   * UNDER-spacing (a false "no collision"), never produce an exact width.
   * Plain geometry, not a design token — the same reasoning MeasurementChart
   * gives for its own MARGIN constants.
   */
  charWidthPx?: number;
  /** Minimum horizontal gap kept between two labels sharing a row before
   *  they are judged to collide and pushed to separate rows. */
  labelGapPx?: number;
  /** The widest a label — icon and text together — may render before the
   *  text is truncated. The full title always survives on `event.title` for
   *  the popover. */
  maxLabelWidthPx?: number;
  /**
   * Width reserved, before the label's own text, for its category icon (or
   * the generic mark drawn when it has none). Part of the row-packing width
   * budget and of `maxLabelWidthPx`'s ceiling — not something the renderer
   * adds on top after the fact, or two labels laid out to just clear each
   * other would end up overlapping the instant an icon is drawn in front of
   * one of them.
   */
  iconWidthPx?: number;
  /**
   * The pixel height of one stacked label row, used only to report each
   * label's own hit-box vertical bounds (`hitY0`/`hitY1`, relative to the
   * top of the lane). Does not affect row ASSIGNMENT — collision packing is
   * purely horizontal — only the y-bounds reported for whichever row a label
   * lands in, so a taller row (a coarse pointer's 44px touch target) is a
   * single number the caller supplies, not a rule this module has to know.
   */
  rowHeightPx?: number;
  /**
   * The most rows of labels to use. Beyond it an event keeps its mark and its
   * place on the axis but is given no label, because the lane above the plot
   * reserves a fixed height and a row drawn past it would sit on the chart.
   */
  maxRows?: number;
  /**
   * A floor under a hit box's own WIDTH (touch-target.dsds.yaml's 44px,
   * under a coarse pointer only — the caller passes 0 on a fine one).
   * Applied after row assignment, growing `hitX1` rightward only; it never
   * feeds back into collision packing, which stays keyed to the label's own
   * drawn width. Two labels short and close enough that only their
   * FLOORED hit boxes touch are accepted, not repacked into separate rows —
   * they resolve by DOM order, the same as any other overlapping hit target.
   */
  minHitWidthPx?: number;
}

const DEFAULT_CHAR_WIDTH_PX = 5.5;
const DEFAULT_LABEL_GAP_PX = 12;
const DEFAULT_MAX_LABEL_WIDTH_PX = 120;
const DEFAULT_ICON_WIDTH_PX = 14;
const DEFAULT_ROW_HEIGHT_PX = 16;
const DEFAULT_MIN_HIT_WIDTH_PX = 0;

interface LaidOutEventBase {
  event: LifeEvent;
  /** The title, truncated to fit inside `maxLabelWidthPx` alongside its
   *  icon; `event.title` still carries the untruncated text for the
   *  popover. */
  labelText: string;
  /** Where the label's own icon+text starts — the event's x for a moment,
   *  the clipped period's own visible start for a period. */
  labelX: number;
  /** Which stacked label row this event's title lands in. Two events whose
   *  labels would otherwise overlap are never given the same row. */
  labelRow: number;
  /**
   * The time actually visible at this event's drawn start — `startsAt`
   * clamped into the chart's own domain. For a period clipped at the left
   * edge this is the domain's own start, never the event's real (off-plot)
   * `startsAt`: a reading nearest to a time before the chart even begins
   * would point at nothing on screen.
   */
  visibleStartMs: number;
  /** The label's own hit-box bounds, icon included — horizontal in plot
   *  pixels, vertical relative to the top of the lane (add the caller's own
   *  lane-top y to place them). Grows only within the row it was assigned;
   *  it is the caller's job to keep that row inside the lane. */
  hitX0: number;
  hitX1: number;
  hitY0: number;
  hitY1: number;
}

/** A moment: a thin vertical line at one x. */
export interface LaidOutMoment extends LaidOutEventBase {
  kind: 'moment';
  x: number;
}

/** A period: a band spanning [x0, x1], already clipped to the plot's own
 *  pixel bounds when the event runs off either edge. */
export interface LaidOutPeriod extends LaidOutEventBase {
  kind: 'period';
  x0: number;
  x1: number;
  /** True when the event's own start lies before the domain, so the band's
   *  left edge is the axis, not the event. */
  clippedStart: boolean;
  /** Same, for the event's own end lying beyond the domain. */
  clippedEnd: boolean;
}

export type LaidOutEvent = LaidOutMoment | LaidOutPeriod;

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

function truncateLabel(title: string, maxWidthPx: number, charWidthPx: number): string {
  const maxChars = Math.max(1, Math.floor(maxWidthPx / charWidthPx));
  if (title.length <= maxChars) return title;
  return `${title.slice(0, Math.max(1, maxChars - 1))}…`;
}

interface Positioned {
  event: LifeEvent;
  kind: 'moment' | 'period';
  x?: number;
  x0?: number;
  x1?: number;
  clippedStart?: boolean;
  clippedEnd?: boolean;
  /** The x the label is judged to start at, for both sorting and collision
   *  packing — the line's own x for a moment, the visible left edge for a
   *  period. */
  anchorX: number;
  /** `startsAt` (period: the earlier of `startsAt`/`endsAt`) clamped into
   *  the domain — see `LaidOutEventBase.visibleStartMs`. */
  visibleStartMs: number;
}

/**
 * Lays out the events that belong on this chart: which are a moment (a thin
 * line) versus a period (a clipped band), where each sits in the plot's own
 * pixel space, and which stacked label row keeps its title clear of its
 * neighbours'.
 *
 * Events are dropped, one at a time and never taking a neighbour with them,
 * when:
 *  - `startsAt` does not parse as a date at all;
 *  - a period's `endsAt` does not parse (the whole event is dropped rather
 *    than silently reinterpreted as a moment);
 *  - the event has no part of itself inside `domain` — entirely before
 *    `from` or entirely after `to`. An event that only PARTLY overlaps the
 *    domain is kept and clipped, never dropped: the half that is in frame is
 *    exactly the context a reader is looking for.
 *
 * A moment sitting exactly on `from` or `to` is kept, at the corresponding
 * plot edge — the alternative is an event that vanishes at one zoom level
 * and reappears at the next.
 */
export function layoutEvents(
  events: readonly LifeEvent[],
  domain: EventLayoutDomain,
  plot: EventLayoutPlot,
  options: EventLayoutOptions = {},
): LaidOutEvent[] {
  const charWidthPx = options.charWidthPx ?? DEFAULT_CHAR_WIDTH_PX;
  const labelGapPx = options.labelGapPx ?? DEFAULT_LABEL_GAP_PX;
  const maxLabelWidthPx = options.maxLabelWidthPx ?? DEFAULT_MAX_LABEL_WIDTH_PX;
  const iconWidthPx = options.iconWidthPx ?? DEFAULT_ICON_WIDTH_PX;
  const rowHeightPx = options.rowHeightPx ?? DEFAULT_ROW_HEIGHT_PX;
  const maxRows = options.maxRows;
  const minHitWidthPx = options.minHitWidthPx ?? DEFAULT_MIN_HIT_WIDTH_PX;
  // The icon sits inside the same ceiling as the text, not on top of it —
  // see the option's own doc comment for why drawing it afterwards would
  // silently bring back the overlap this module exists to prevent.
  const textMaxWidthPx = Math.max(0, maxLabelWidthPx - iconWidthPx);

  const minT = Date.parse(domain.from);
  const maxT = Date.parse(domain.to);
  if (!Number.isFinite(minT) || !Number.isFinite(maxT) || plot.x1 <= plot.x0) return [];

  const xScale = (t: number): number =>
    minT === maxT ? (plot.x0 + plot.x1) / 2 : plot.x0 + ((t - minT) / (maxT - minT)) * (plot.x1 - plot.x0);

  const positioned: Positioned[] = [];
  for (const event of events) {
    const startT = Date.parse(event.startsAt);
    // An unparseable date is dropped on its own — it never takes the rest
    // of the batch down with it.
    if (Number.isNaN(startT)) continue;

    const period = isPeriod(event);
    let endT = startT;
    if (period) {
      endT = Date.parse(event.endsAt as string);
      if (Number.isNaN(endT)) continue;
    }

    const spanStart = Math.min(startT, endT);
    const spanEnd = Math.max(startT, endT);
    // No part of the event falls inside the domain: dropped, not clipped to
    // nothing.
    if (spanStart > maxT || spanEnd < minT) continue;

    if (!period) {
      const x = clamp(xScale(startT), plot.x0, plot.x1);
      positioned.push({ event, kind: 'moment', x, anchorX: x, visibleStartMs: clamp(startT, minT, maxT) });
    } else {
      const rawX0 = xScale(spanStart);
      const rawX1 = xScale(spanEnd);
      const x0 = clamp(rawX0, plot.x0, plot.x1);
      const x1 = clamp(rawX1, plot.x0, plot.x1);
      positioned.push({
        event,
        kind: 'period',
        x0,
        x1,
        clippedStart: rawX0 < plot.x0,
        clippedEnd: rawX1 > plot.x1,
        // The label anchors to the visible portion, per the drawing rules —
        // never to the (possibly off-plot) real start of the event.
        anchorX: x0,
        // The clock time at that same visible edge — the domain's own start
        // when clipped there, never the event's real (off-plot) startsAt.
        visibleStartMs: clamp(spanStart, minT, maxT),
      });
    }
  }

  // Row assignment is a classic greedy interval-packing pass: process
  // left-to-right, and put each label in the first row whose last label
  // already ends (with a gap) before this one starts. Two labels that would
  // overlap can never land in the same row; two that do not overlap always
  // share the lowest row available, so the lane stays as short as it can.
  positioned.sort((a, b) => a.anchorX - b.anchorX);

  const rowEnds: number[] = [];
  const result: LaidOutEvent[] = [];
  for (const p of positioned) {
    let labelText = truncateLabel(p.event.title, textMaxWidthPx, charWidthPx);
    // The icon's width is reserved here, in the same number collision
    // packing uses — not added by the renderer afterwards, which is exactly
    // how a label laid out to just clear its neighbour would end up
    // overlapping it the moment an icon is drawn in front.
    const labelWidth = iconWidthPx + Math.min(textMaxWidthPx, p.event.title.length * charWidthPx);

    /**
     * Where the label actually starts.
     *
     * An event near the end of the range anchors close to the right edge, and
     * a label left-anchored there simply runs off the chart and gets cut mid
     * word — no ellipsis, just a sliced letter. Pulled back so its far end
     * lands on the plot's edge instead. The axis ticks and the endpoint label
     * in MeasurementChart already do this for themselves; the event labels
     * were the one thing in that lane that did not.
     *
     * Never pulled left of the plot: a label wider than the whole chart keeps
     * its start and lets truncation do the rest.
     */
    const labelX = Math.max(plot.x0, Math.min(p.anchorX, plot.x1 - labelWidth));

    let row = rowEnds.findIndex((end) => labelX >= end + labelGapPx);
    if (row === -1) {
      if (maxRows !== undefined && rowEnds.length >= maxRows) {
        // Out of rows. The event still belongs on the axis, so it keeps its
        // mark; only its title goes. Placed on the last row so its hit box is
        // still somewhere sensible rather than off the top of the lane.
        row = maxRows - 1;
        labelText = '';
      } else {
        row = rowEnds.length;
        rowEnds.push(labelX + labelWidth);
      }
    } else {
      rowEnds[row] = labelX + labelWidth;
    }

    const hitX0 = labelX;
    // The touch-target floor grows the hit box rightward only, after row
    // assignment — it never feeds back into collision packing (see the
    // option's own doc comment).
    const hitX1 = labelX + Math.max(labelWidth, minHitWidthPx);
    const hitY0 = row * rowHeightPx;
    const hitY1 = hitY0 + rowHeightPx;

    if (p.kind === 'moment') {
      result.push({
        event: p.event,
        kind: 'moment',
        x: p.x as number,
        labelText,
        labelX,
        labelRow: row,
        visibleStartMs: p.visibleStartMs,
        hitX0,
        hitX1,
        hitY0,
        hitY1,
      });
    } else {
      result.push({
        event: p.event,
        kind: 'period',
        x0: p.x0 as number,
        x1: p.x1 as number,
        clippedStart: p.clippedStart as boolean,
        clippedEnd: p.clippedEnd as boolean,
        labelText,
        labelX,
        labelRow: row,
        visibleStartMs: p.visibleStartMs,
        hitX0,
        hitX1,
        hitY0,
        hitY1,
      });
    }
  }
  return result;
}
